import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import cloud from "d3-cloud";
import { AnimatePresence, motion } from "framer-motion";
import { Field } from "@/components/diagnostico/Field";
import { formatDate, type Submission } from "@/lib/diagnostico";
import { clearToken, fetchDiagnosticos, getToken, loginAdmin } from "@/lib/api";
import logo from "@/assets/logo.png";

export const Route = createFileRoute("/admin")({
  head: () => ({
    meta: [
      { title: "Painel do Administrador | Diagnósticos Tributários" },
      {
        name: "description",
        content:
          "Painel executivo com métricas e respostas completas dos diagnósticos tributários enviados.",
      },
      { property: "og:title", content: "Painel do Administrador" },
      { property: "og:description", content: "Métricas e respostas dos diagnósticos tributários." },
    ],
  }),
  component: Admin,
});

function Admin() {
  const [authed, setAuthed] = useState(false);
  const [ready, setReady] = useState(false);
  const [items, setItems] = useState<Submission[]>([]);

  const loadItems = async () => {
    try {
      const data = await fetchDiagnosticos();
      setItems(data);
    } catch {
      // token expirado ou inválido
      clearToken();
      setAuthed(false);
    }
  };

  useEffect(() => {
    const hasToken = !!getToken();
    setAuthed(hasToken);
    if (hasToken) loadItems();
    setReady(true);
  }, []);

  if (!ready) return <main className="min-h-screen" />;

  if (!authed) {
    return (
      <Login
        onSuccess={async () => {
          setAuthed(true);
          await loadItems();
        }}
      />
    );
  }

  return (
    <Dashboard
      items={items}
      onLogout={() => {
        clearToken();
        setAuthed(false);
        setItems([]);
      }}
    />
  );
}

function Login({ onSuccess }: { onSuccess: () => Promise<void> }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  return (
    <main className="flex min-h-screen items-center justify-center px-4 py-16">
      <motion.form
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        onSubmit={async (e) => {
          e.preventDefault();
          setLoading(true);
          setError("");
          try {
            await loginAdmin(email.trim().toLowerCase(), password);
            await onSuccess();
          } catch {
            setError("Credenciais inválidas.");
          } finally {
            setLoading(false);
          }
        }}
        className="glass-panel w-full max-w-sm rounded-3xl p-8"
      >
        <div className="mb-8 flex flex-col items-center text-center">
          <span className="mb-5 flex size-12 items-center justify-center rounded-2xl gradient-primary glow-primary">
            <i className="fi fi-tr-lock-alt text-2xl leading-none flex items-center justify-center text-primary-foreground" />
          </span>
          <h1 className="text-xl font-bold">Área do Administrador</h1>
          <p className="mt-2 text-xs text-muted-foreground">
            Acesso restrito à equipe de diagnóstico.
          </p>
        </div>
        <div className="space-y-4">
          <Field
            label="E-mail"
            type="email"
            placeholder="admin@exemplo.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Field
            label="Senha"
            type="password"
            placeholder="••••••••"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
        {error ? <p className="mt-4 text-sm text-destructive">{error}</p> : null}
        <motion.button
          whileHover={{ scale: loading ? 1 : 1.02 }}
          whileTap={{ scale: loading ? 1 : 0.98 }}
          type="submit"
          disabled={loading}
          className="mt-7 w-full rounded-xl gradient-primary px-6 py-3 text-sm font-semibold text-primary-foreground glow-primary disabled:opacity-70 disabled:pointer-events-none"
        >
          {loading ? "Entrando…" : "Entrar"}
        </motion.button>
        <Link
          to="/"
          className="mt-5 block text-center text-xs text-muted-foreground hover:text-primary"
        >
          Voltar ao diagnóstico
        </Link>
      </motion.form>
    </main>
  );
}

function Dashboard({ items, onLogout }: { items: Submission[]; onLogout: () => void }) {
  const [active, setActive] = useState<Submission | null>(null);

  const stats = useMemo(() => {
    const regimes = new Map<string, number>();
    const setores = new Map<string, number>();
    for (const i of items) {
      regimes.set(i.regime, (regimes.get(i.regime) ?? 0) + 1);
      setores.set(i.setor, (setores.get(i.setor) ?? 0) + 1);
    }
    return {
      total: items.length,
      regimes: [...regimes.entries()].sort((a, b) => b[1] - a[1]),
      setores: [...setores.entries()].sort((a, b) => b[1] - a[1]),
    };
  }, [items]);

  return (
    <main className="min-h-screen px-4 py-8 sm:px-8 sm:py-12">
      <div className="mx-auto w-full max-w-6xl">
        <header className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src={logo} alt="Logotipo Giants" className="h-16 w-auto sm:h-28" />
            <span className="text-xs font-medium uppercase tracking-[0.24em] text-muted-foreground">
              Painel executivo
            </span>
          </div>
          <button
            onClick={onLogout}
            className="inline-flex items-center gap-2 rounded-xl border border-border px-4 py-2.5 text-xs font-medium text-muted-foreground transition-colors hover:border-primary/40 hover:text-primary"
          >
            <i className="fi fi-tr-sign-out-alt text-sm leading-none" /> Sair
          </button>
        </header>

        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          <Kpi
            icon={<i className="fi fi-tr-building leading-none" />}
            label="Empresas diagnosticadas"
            value={String(stats.total)}
          />
          <div className="glass-panel rounded-2xl p-5">
            <p className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              <i className="fi fi-tr-chart-pie leading-none text-primary" /> Distribuição por regime
            </p>
            <ul className="mt-4 space-y-2">
              {stats.regimes.map(([regime, count]) => (
                <li key={regime} className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">{regime}</span>
                  <span className="font-semibold text-primary">{count}</span>
                </li>
              ))}
              {stats.regimes.length === 0 && (
                <li className="text-sm text-muted-foreground">Sem dados</li>
              )}
            </ul>
          </div>
        </div>

        <div className="mt-4 grid gap-4 sm:grid-cols-5">
          <div className="sm:col-span-3">
            <WordCloud setores={stats.setores} />
          </div>
          <div className="sm:col-span-2">
            <SegmentCard setores={stats.setores} total={stats.total} />
          </div>
        </div>

        <section className="glass-panel mt-6 overflow-hidden rounded-2xl">
          <div className="border-b border-border px-5 py-4">
            <h2 className="text-sm font-semibold tracking-wide">Respostas recebidas</h2>
          </div>

          {/* Mobile: cards */}
          <div className="sm:hidden max-h-[480px] overflow-y-auto divide-y divide-border">
            {items.length === 0 && (
              <p className="px-5 py-8 text-center text-sm text-muted-foreground">Sem respostas</p>
            )}
            {items.map((i) => (
              <button
                key={i.id}
                onClick={() => setActive(i)}
                className="w-full text-left px-5 py-4 transition-colors hover:bg-primary/5 active:bg-primary/10"
              >
                <div className="flex items-start justify-between gap-2">
                  <span className="font-medium text-sm leading-tight">{i.empresa}</span>
                  <span className="text-xs font-semibold text-primary shrink-0">
                    {i.faturamento}
                  </span>
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs text-muted-foreground">
                  <span>{i.nomeResponsavel}</span>
                  {i.cargoResponsavel && (
                    <>
                      <span>·</span>
                      <span>{i.cargoResponsavel}</span>
                    </>
                  )}
                </div>
                <div className="mt-1 flex items-center justify-between gap-2 text-[10px] text-muted-foreground">
                  <span>{i.setor}</span>
                  <span>{formatDate(i.createdAt)}</span>
                </div>
              </button>
            ))}
          </div>

          {/* Desktop: table */}
          <div className="hidden sm:block max-h-[480px] overflow-auto">
            <table className="w-full min-w-[720px] text-left text-sm">
              <thead className="sticky top-0 z-10">
                <tr className="bg-[oklch(0.19_0.006_285)] text-[11px] uppercase tracking-[0.16em] text-muted-foreground shadow-[0_1px_0_0_oklch(1_0_0/10%)]">
                  <th className="px-5 py-3 font-medium">Data</th>
                  <th className="px-5 py-3 font-medium">Empresa</th>
                  <th className="px-5 py-3 font-medium">Responsável</th>
                  <th className="px-5 py-3 font-medium">Faturamento</th>
                  <th className="px-5 py-3 font-medium">Setor</th>
                </tr>
              </thead>
              <tbody>
                {items.map((i) => (
                  <tr
                    key={i.id}
                    onClick={() => setActive(i)}
                    className="cursor-pointer border-t border-border transition-colors hover:bg-primary/5"
                  >
                    <td className="px-5 py-4 text-muted-foreground">{formatDate(i.createdAt)}</td>
                    <td className="px-5 py-4 font-medium">{i.empresa}</td>
                    <td className="px-5 py-4">
                      <span className="font-medium">{i.nomeResponsavel}</span>
                      {i.cargoResponsavel && (
                        <span className="block text-xs text-muted-foreground">
                          {i.cargoResponsavel}
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-4 text-primary">{i.faturamento}</td>
                    <td className="px-5 py-4 text-muted-foreground">{i.setor}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setActive(null)}
            className="fixed inset-0 z-50 flex items-end justify-center bg-background/80 p-0 backdrop-blur-sm sm:items-center sm:p-6"
          >
            <motion.div
              initial={{ opacity: 0, y: 40, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 30, scale: 0.98 }}
              transition={{ type: "spring", stiffness: 260, damping: 26 }}
              onClick={(e) => e.stopPropagation()}
              className="glass-panel max-h-[88vh] w-full max-w-2xl overflow-y-auto rounded-t-3xl p-6 sm:rounded-3xl sm:p-8"
            >
              <div className="mb-6 flex items-start justify-between gap-4">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-primary">
                    Raio-X completo
                  </p>
                  <h3 className="mt-2 text-xl font-bold">{active.empresa}</h3>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Enviado em {formatDate(active.createdAt)}
                  </p>
                </div>
                <button
                  onClick={() => setActive(null)}
                  className="rounded-lg border border-border p-2 text-muted-foreground transition-colors hover:text-primary"
                >
                  <i className="fi fi-tr-x leading-none" />
                </button>
              </div>
              <dl className="grid gap-3 sm:grid-cols-2">
                <Row label="Nome do responsável" value={active.nomeResponsavel} />
                <Row label="Cargo" value={active.cargoResponsavel || "—"} />
                <Row label="Faturamento anual" value={active.faturamento} />
                <Row label="E-mail" value={active.email} />
                <Row label="WhatsApp" value={active.whatsapp || "—"} />
                <Row label="Setor" value={active.setor} />
                <Row label="Regime tributário" value={active.regime} />
                <Row label="Carga tributária" value={active.carga} />
                <Row label="Margem de lucro" value={active.margem} />
                <Row label="Maior custo" value={active.custos} />
                <Row label="Mão de obra" value={active.maoDeObra} />
                <Row label="Perfil de clientes" value={active.perfilClientes} />
                <div className="min-w-0 rounded-xl border border-border bg-card/40 px-4 py-3 sm:col-span-2">
                  <dt className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                    Momento tributário
                  </dt>
                  <dd className="mt-1.5 break-words text-sm leading-snug">{active.momento}</dd>
                </div>
              </dl>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}

interface CloudWord extends cloud.Word {
  value: number;
}

function WordCloud({ setores }: { setores: [string, number][] }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [layoutWords, setLayoutWords] = useState<CloudWord[]>([]);
  const [svgWidth, setSvgWidth] = useState(600);
  const HEIGHT = 320;

  const maxCount = Math.max(...setores.map(([, c]) => c), 1);
  const minCount = Math.min(...setores.map(([, c]) => c), 1);

  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver((entries) => {
      setSvgWidth(Math.floor(entries[0]!.contentRect.width));
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (setores.length === 0 || svgWidth === 0) return;

    const range = maxCount - minCount || 1;
    // cap dinâmico: palavras longas ficam limitadas ao tamanho máximo que cabe horizontalmente
    const fontSize = (count: number, text: string) => {
      const base = Math.round(11 + ((count - minCount) / range) * 34);
      if (text.length <= 10) return base;
      const widthCap = Math.floor((svgWidth * 0.8) / (text.length * 0.6));
      return Math.min(base, Math.max(widthCap, 11));
    };

    const layout = cloud<CloudWord>()
      .size([svgWidth, HEIGHT])
      .words(setores.map(([text, value]) => ({ text, value, size: fontSize(value, text) })))
      .padding(4)
      .rotate((d) => {
        if ((d.text?.length ?? 0) > 14) return 0;
        const r = Math.random();
        return r < 0.5 ? 0 : r < 0.75 ? 90 : -90;
      })
      .font("Montserrat")
      .fontSize((d) => d.size!)
      .on("end", (words) => setLayoutWords(words as CloudWord[]))
      .start();

    return () => {
      layout.stop();
    };
  }, [setores, svgWidth]);

  return (
    <div className="glass-panel h-full rounded-2xl p-5">
      <p className="mb-2 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
        <i className="fi fi-tr-layers leading-none text-primary" /> Segmentos atendidos
      </p>
      <div ref={containerRef} className="w-full">
        {setores.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">Sem dados</p>
        ) : (
          <svg width={svgWidth} height={HEIGHT}>
            <g transform={`translate(${svgWidth / 2},${HEIGHT / 2})`}>
              {layoutWords.map((w) => {
                const ratio =
                  maxCount <= minCount ? 1 : (w.value - minCount) / (maxCount - minCount);
                // interpola oklch: cinza escuro (L=0.48, C=0.01) → dourado vivo (L=0.82, C=0.185)
                const l = (0.48 + ratio * 0.34).toFixed(3);
                const c = (0.01 + ratio * 0.175).toFixed(3);
                return (
                  <text
                    key={w.text}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    transform={`translate(${w.x ?? 0},${w.y ?? 0}) rotate(${w.rotate ?? 0})`}
                    fontSize={w.size}
                    fontFamily="DM Sans, sans-serif"
                    fontWeight="600"
                    fill={`oklch(${l} ${c} 84)`}
                    style={{ cursor: "default" }}
                  >
                    <title>{`${w.value} diagnóstico${w.value !== 1 ? "s" : ""}`}</title>
                    {w.text}
                  </text>
                );
              })}
            </g>
          </svg>
        )}
      </div>
    </div>
  );
}

function SegmentCard({ setores, total }: { setores: [string, number][]; total: number }) {
  const max = Math.max(...setores.map(([, c]) => c), 1);
  return (
    <div className="glass-panel h-full rounded-2xl p-5">
      <p className="mb-3 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
        <i className="fi fi-tr-chart-histogram leading-none text-primary" /> Contagem por segmento
      </p>
      {setores.length === 0 ? (
        <p className="py-8 text-center text-sm text-muted-foreground">Sem dados</p>
      ) : (
        <ul className="space-y-2.5 overflow-y-auto" style={{ maxHeight: 228 }}>
          {setores.map(([setor, count]) => (
            <li key={setor}>
              <div className="mb-1 flex items-center justify-between gap-2">
                <span className="min-w-0 truncate text-xs font-medium" title={setor}>
                  {setor}
                </span>
                <span className="shrink-0 text-xs font-bold text-primary">{count}</span>
              </div>
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-primary/10">
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${(count / max) * 100}%`,
                    background: "var(--gradient-primary)",
                    opacity: 0.4 + (count / max) * 0.6,
                  }}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
      {total > 0 && (
        <p className="mt-3 border-t border-border pt-3 text-[10px] text-muted-foreground">
          {setores.length} segmento{setores.length !== 1 ? "s" : ""} em {total} diagnóstico
          {total !== 1 ? "s" : ""}
        </p>
      )}
    </div>
  );
}

function Kpi({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="glass-panel rounded-2xl p-5">
      <p className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
        <span className="text-primary">{icon}</span> {label}
      </p>
      <p className="mt-4 text-2xl font-bold tracking-tight">{value}</p>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0 rounded-xl border border-border bg-card/40 px-4 py-3">
      <dt className="truncate text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
        {label}
      </dt>
      <dd className="mt-1.5 truncate text-sm leading-snug" title={value}>
        {value}
      </dd>
    </div>
  );
}
